import { OnePieceSet, OnePieceCard } from '../types/onepiece';
import { PokemonSet } from '../types/pokemon';

const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:3001';

class TcgDataService {
  /**
   * Fetch all Pokemon master sets
   */
  async getPokemonSets(): Promise<PokemonSet[]> {
    try {
      const response = await fetch(`${API_BASE_URL}/api/public/sets/pokemon`);
      if (!response.ok) {
        throw new Error(`Failed to fetch Pokemon sets: ${response.statusText}`);
      }
      return await response.json();
    } catch (error) {
      console.error('Error fetching Pokemon sets:', error);
      return [];
    }
  }

  /**
   * Fetch all One Piece master sets
   */
  async getOnePieceSets(): Promise<OnePieceSet[]> {
    try {
      const response = await fetch(`${API_BASE_URL}/api/public/sets/onepiece`);
      if (!response.ok) {
        throw new Error(`Failed to fetch One Piece sets: ${response.statusText}`);
      }
      return await response.json();
    } catch (error) {
      console.error('Error fetching One Piece sets:', error);
      return [];
    }
  }

  /**
   * Fetch One Piece starter decks
   */
  async getOnePieceStarterDecks(): Promise<any[]> {
    try {
      const response = await fetch(`${API_BASE_URL}/api/public/sets/onepiece/starter`);
      if (!response.ok) {
        throw new Error(`Failed to fetch One Piece starter decks: ${response.statusText}`);
      }
      return await response.json();
    } catch (error) {
      console.error('Error fetching One Piece starter decks:', error);
      return [];
    }
  }

  /**
   * Fetch cards for a specific Pokemon set
   */
  async getPokemonSetCards(setId: string): Promise<PokemonSet[]> {
    try {
      const response = await fetch(`${API_BASE_URL}/api/public/sets/pokemon/${setId}/cards`);
      if (!response.ok) {
        throw new Error(`Failed to fetch Pokemon cards for set ${setId}: ${response.statusText}`);
      }
      return await response.json();
    } catch (error) {
      console.error(`Error fetching Pokemon cards for set ${setId}:`, error);
      return [];
    }
  }

  /**
   * Fetch cards for a specific One Piece set
   */
  async getOnePieceSetCards(setId: string): Promise<OnePieceCard[]> {
    try {
      const response = await fetch(`${API_BASE_URL}/api/public/sets/onepiece/${setId}/cards`);
      if (!response.ok) {
        throw new Error(`Failed to fetch One Piece cards for set ${setId}: ${response.statusText}`);
      }
      return await response.json();
    } catch (error) {
      console.error(`Error fetching One Piece cards for set ${setId}:`, error);
      return [];
    }
  }

  /**
   * Fetch One Piece sealed products
   */
  async getOnePieceSealedProducts(): Promise<any[]> {
    try {
      const response = await fetch(`${API_BASE_URL}/api/public/sets/onepiece/sealed`);
      if (!response.ok) {
        throw new Error(`Failed to fetch One Piece sealed products: ${response.statusText}`);
      }
      return await response.json();
    } catch (error) {
      console.error('Error fetching One Piece sealed products:', error);
      return [];
    }
  }

  /**
   * Prefetch pricing for chase cards in a set
   */
  async prefetchPricing(setId: string, game: 'pokemon' | 'onepiece'): Promise<any> {
    try {
      const response = await fetch(`${API_BASE_URL}/api/card-pricing/prefetch`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ setId, game })
      });
      if (!response.ok) {
        throw new Error(`Failed to prefetch pricing: ${response.statusText}`);
      }
      return await response.json();
    } catch (error) {
      console.error('Error prefetching pricing:', error);
      return null;
    }
  }
}

export const tcgDataService = new TcgDataService();
